/**
 * RecentlyEditedStrip — horizontal audit strip at the top of the
 * data-gap-reports page. Pulls from GET /api/admin/field-edits/recent
 * and renders each edit with a colour-coded actor chip:
 *
 *   Staff  → purple  (matches WUA brand)
 *   Uni    → blue    (matches uni-portal claim brand)
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

function fmtDelta(before, after) {
  const b = before == null ? "∅" : String(before);
  const a = after == null ? "∅" : String(after);
  return (
    <span className="font-mono text-[11px]">
      <span className="text-stone-400 line-through mr-1">{b}</span>
      <span className="text-stone-900">{a}</span>
    </span>
  );
}

function ago(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(0, Math.floor((now - then) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch {
    return "";
  }
}

export default function RecentlyEditedStrip({ refreshKey }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          "/admin/field-edits/recent?limit=15",
        );
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled)
          setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (err) {
    return (
      <div className="mt-4 text-[11px] text-rose-700">
        Recently edited: {err}
      </div>
    );
  }
  if (items === null) {
    return (
      <div className="mt-4 text-[11px] text-stone-500">
        <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
        Loading recent edits…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div
        className="mt-4 text-[11px] text-stone-500 italic"
        data-testid="recently-edited-strip-empty"
      >
        No field edits recorded yet.
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-2">
        Recently edited
      </div>
      <div
        className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin"
        data-testid="recently-edited-strip"
      >
        {items.map((it) => {
          const isUni = it.actor_type === "university";
          return (
            <div
              key={it.id}
              data-testid={`recently-edited-item-${it.id}`}
              className="shrink-0 w-64 border border-stone-200 bg-white rounded-lg p-2.5 text-[11px]"
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`text-[9px] px-1.5 py-0.5 rounded font-underground-sc tracking-wider ${
                    isUni
                      ? "bg-blue-50 text-blue-800 border border-blue-200"
                      : "bg-purple-50 text-purple-800 border border-purple-200"
                  }`}
                  data-testid={`recently-edited-chip-${it.id}`}
                >
                  {isUni ? "University" : "Staff"}
                </span>
                <span className="text-stone-400 font-mono text-[10px]">
                  {ago(it.created_at)}
                </span>
              </div>
              <div className="mt-1.5 text-[11px] text-stone-800 font-medium truncate">
                {it.field_label}
              </div>
              <div className="text-[10px] text-stone-500 truncate">
                {it.doc_title || it.doc_id}
                {it.university_name && it.collection === "degrees" && (
                  <span> · {it.university_name}</span>
                )}
              </div>
              <div className="mt-1">{fmtDelta(it.before, it.after)}</div>
              {it.actor_email && (
                <div className="mt-1 text-[9px] text-stone-400 font-mono truncate">
                  by {it.actor_email}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
